import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

const buttonVariants = {
  variant: {
    primary:
      'bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-ambient hover:scale-[1.02] hover:shadow-lg transition-all duration-fast',
    secondary:
      'border border-ghost bg-transparent hover:bg-surface-container-high hover:text-on-surface text-text-primary transition-all duration-fast',
    ghost:
      'bg-transparent hover:bg-surface-container hover:text-on-surface text-text-secondary transition-all duration-fast hover:scale-[1.02]',
    danger:
      'bg-error text-surface hover:bg-error/90 active:bg-error/80 shadow-ambient hover:scale-[1.02] transition-transform duration-fast',
    outline:
      'border border-ghost-strong bg-transparent hover:bg-surface-container hover:text-on-surface text-text-primary transition-all duration-fast',
    link: 'text-primary underline-offset-4 hover:underline transition-all',
  },
  size: {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  },
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: keyof typeof buttonVariants.variant;
  size?: keyof typeof buttonVariants.size;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'default', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-bg-surface transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
          buttonVariants.variant[variant],
          buttonVariants.size[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
